import 'react-phone-number-input/style.css';

import { useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import { useMemo } from 'react';
import { Col, Form } from 'react-bootstrap';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';

import type { IForm, IFormField } from '@customTypes/form';
import { camelToTitleCase } from '@helpers/string';

const generateName = (name: string, prefix = ''): string => {
  return prefix ? `${prefix}.${name}` : name;
};

export default function PhoneField<T extends IForm>({
  prefix = '',
  name,
  cols,
  className = '',
  label,
  disabled = false,
  required = false,
  helperText = '',
  labelClassName = '',
  dataTestId,
  phiHidden,
  phiSanitize,
}: Omit<IFormField<T>, 'type' | 'address' | 'inputProps'> & {
  labelClassName?: string;
  dataTestId?: string;
  /** When true, fully hidden in OpenReplay replay. */
  phiHidden?: boolean;
  /** When true, masked in OpenReplay replay. */
  phiSanitize?: boolean;
}) {
  const { touched, errors, values, handleBlur, setFieldValue } = useFormikContext<T>();

  const groupClassName = useMemo(() => {
    let colClass = '';
    if (cols === 4) colClass = 'col-12 col-lg-4';
    else if (cols === 6) colClass = 'col-12 col-lg-6';
    else if (cols === 8) colClass = 'col-12 col-lg-8';

    return `${colClass} ${className}`.trim();
  }, [cols, className]);

  const formControlName = useMemo(() => generateName(name, prefix), [prefix, name]);
  const error = useMemo(() => get(errors, formControlName), [formControlName, errors]);
  const isDirty = useMemo(() => get(touched, formControlName), [formControlName, touched]);
  const fieldValue = useMemo(() => get(values, formControlName), [values, formControlName]);
  const isInvalid = useMemo(() => {
    const value = fieldValue as string | undefined;
    return !!value && !isPossiblePhoneNumber(value);
  }, [fieldValue]);

  const displayError = useMemo(() => {
    if (isDirty && error) return error as string;
    if (isDirty && isInvalid) return 'Please provide a valid phone number.';
    return null;
  }, [error, isDirty, isInvalid]);

  const handleChange = (value: string | undefined) => {
    setFieldValue(formControlName, value ?? '');
  };

  const handleBlurEvent = () => {
    handleBlur({ target: { name: formControlName } } as any);
  };

  return (
    <Form.Group as={Col} controlId={`input_${formControlName}`} className={groupClassName}>
      <Form.Label className={labelClassName}>
        {required && (
          <span className='req' aria-hidden='true'>
            *{' '}
          </span>
        )}
        {label ?? camelToTitleCase(camelCase(name))}
      </Form.Label>
      <PhoneInput
        id={`input_${formControlName}`}
        name={formControlName}
        disabled={disabled}
        required={required}
        placeholder={helperText || undefined}
        numberInputProps={{
          className: `form-control ${displayError ? 'is-invalid' : ''} ${isDirty && !displayError ? 'is-valid' : ''}`,
          'data-testid': dataTestId,
          ...(phiHidden ? { 'data-openreplay-hidden': true } : phiSanitize ? { 'data-openreplay-sanitize': true } : {}),
        }}
        style={{ width: '100%' }}
        onBlur={handleBlurEvent}
        defaultCountry='US'
        value={fieldValue as string | undefined}
        onChange={handleChange}
        aria-invalid={!!displayError}
        aria-describedby={helperText ? `input_${formControlName}Desc` : undefined}
      />

      {displayError && (
        <div className='po-error inline' role='alert'>
          {displayError}
        </div>
      )}
    </Form.Group>
  );
}
